import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  background: var(--carbon-gradient);
  padding: var(--space-16) 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-4xl);
  font-weight: var(--font-black);
  margin-bottom: var(--space-4);
  
  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const HeroSubtitle = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-lg);
  font-weight: var(--font-medium);
  max-width: 600px;
  margin: 0 auto;
  line-height: var(--leading-relaxed);
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const Section = styled.section`
  background: var(--white);
  border-radius: var(--radius-2xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-lg);
  margin-bottom: var(--space-8);
  
  @media (max-width: 768px) {
    padding: var(--space-8);
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-6);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 40px;
    height: 3px;
    background: var(--racing-gradient);
    border-radius: var(--radius-full);
  }
`;

const SectionContent = styled.div`
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--dark-gray);
  
  p {
    margin-bottom: var(--space-4);
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);
    
    li {
      margin-bottom: var(--space-2);
    }
  }
  
  strong {
    color: var(--carbon-black);
    font-weight: var(--font-semibold);
  }
`;

const LastUpdated = styled.div`
  background: var(--light-gray);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  text-align: center;
  font-size: var(--text-sm);
  color: var(--medium-gray);
  margin-bottom: var(--space-8);
`;

const ContactBox = styled.div`
  background: var(--racing-gradient);
  color: var(--white);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  text-align: center;
  margin-top: var(--space-8);
`;

const ContactTitle = styled.h3`
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  margin-bottom: var(--space-3);
`;

const ContactText = styled.p`
  margin-bottom: var(--space-3);
  opacity: 0.9;
`;

const ContactEmail = styled.a`
  color: var(--white);
  font-weight: var(--font-semibold);
  text-decoration: underline;
  
  &:hover {
    color: var(--chrome-silver);
  }
`;

const PrivacyPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Privacy Policy</HeroTitle>
          <HeroSubtitle>
            Your privacy is important to us. This policy explains how we collect, 
            use, and protect your personal information.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        <LastUpdated>
          Last updated: January 1, 2024
        </LastUpdated>

        <Section>
          <SectionTitle>Introduction</SectionTitle>
          <SectionContent>
            <p>
              Welcome to CarBuzz.fit ("we," "our," or "us"). This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you visit our website carbuzz.fit, 
              including any other media form, media channel, mobile website, or mobile application 
              related or connected thereto (collectively, the "Site").
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with the terms of this 
              Privacy Policy, please do not access the Site. We reserve the right to make changes to 
              this Privacy Policy at any time and for any reason. We will alert you about any changes 
              by updating the "Last updated" date of this Privacy Policy.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Information We Collect</SectionTitle>
          <SectionContent>
            <p>
              We may collect information about you in a variety of ways. The information we may collect 
              on the Site includes:
            </p>
            
            <p><strong>Personal Data</strong></p>
            <p>
              Personally identifiable information, such as your name, shipping address, email address, 
              and telephone number, and demographic information, such as your age, gender, hometown, 
              and interests, that you voluntarily give to us when you register with the Site or when 
              you choose to participate in various activities related to the Site.
            </p>

            <p><strong>Derivative Data</strong></p>
            <p>
              Information our servers automatically collect when you access the Site, such as your IP 
              address, your browser type, your operating system, your access times, and the pages you 
              have viewed directly before and after accessing the Site.
            </p>

            <p><strong>Financial Data</strong></p>
            <p>
              Financial information, such as data related to your payment method (e.g., valid credit 
              card number, card brand, expiration date) that we may collect when you purchase, order, 
              return, exchange, or request information about our services from the Site.
            </p>

            <p><strong>Mobile Device Data</strong></p>
            <p>
              Device information, such as your mobile device ID, model, and manufacturer, and information 
              about the location of your device, if you access the Site from a mobile device.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>How We Use Your Information</SectionTitle>
          <SectionContent>
            <p>
              Having accurate information about you permits us to provide you with a smooth, efficient, 
              and customized experience. Specifically, we may use information collected about you via 
              the Site to:
            </p>
            <ul>
              <li>Create and manage your account</li>
              <li>Process your transactions and send you related information</li>
              <li>Email you regarding your account or order</li>
              <li>Fulfill and manage purchases, orders, payments, and other transactions</li>
              <li>Generate a personal profile about you to make future visits more personalized</li>
              <li>Increase the efficiency and operation of the Site</li>
              <li>Monitor and analyze usage and trends to improve your experience</li>
              <li>Notify you of updates to the Site</li>
              <li>Offer new products, services, mobile applications, and/or recommendations</li>
              <li>Perform other business activities as needed</li>
              <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity</li>
              <li>Process payments and refunds</li>
              <li>Request feedback and contact you about your use of the Site</li>
              <li>Resolve disputes and troubleshoot problems</li>
              <li>Respond to product and customer service requests</li>
              <li>Send you a newsletter</li>
              <li>Solicit support for the Site</li>
            </ul>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Disclosure of Your Information</SectionTitle>
          <SectionContent>
            <p>
              We may share information we have collected about you in certain situations. Your information 
              may be disclosed as follows:
            </p>

            <p><strong>By Law or to Protect Rights</strong></p>
            <p>
              If we believe the release of information about you is necessary to respond to legal process, 
              to investigate or remedy potential violations of our policies, or to protect the rights, 
              property, and safety of others, we may share your information as permitted or required by 
              any applicable law, rule, or regulation.
            </p>

            <p><strong>Business Transfers</strong></p>
            <p>
              We may share or transfer your information in connection with, or during negotiations of, 
              any merger, sale of company assets, financing, or acquisition of all or a portion of our 
              business to another company.
            </p>

            <p><strong>Third-Party Service Providers</strong></p>
            <p>
              We may share your information with third parties that perform services for us or on our 
              behalf, including payment processing, data analysis, email delivery, hosting services, 
              customer service, and marketing assistance.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Tracking Technologies</SectionTitle>
          <SectionContent>
            <p><strong>Cookies and Web Beacons</strong></p>
            <p>
              We may use cookies, web beacons, tracking pixels, and other tracking technologies on the 
              Site to help customize the Site and improve your experience. When you access the Site, 
              your personal information is not collected through the use of tracking technology.
            </p>

            <p><strong>Internet-Based Advertising</strong></p>
            <p>
              Additionally, we may use third-party software to serve ads on the Site, implement email 
              marketing campaigns, and manage other interactive marketing initiatives. This third-party 
              software may use cookies or similar tracking technology to help manage and optimize your 
              online experience with us.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Third-Party Websites</SectionTitle>
          <SectionContent>
            <p>
              The Site may contain links to third-party websites and applications of interest, including 
              advertisements and external services, that are not affiliated with us. Once you have used 
              these links to leave the Site, any information you provide to these third parties is not 
              covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your 
              information.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Security of Your Information</SectionTitle>
          <SectionContent>
            <p>
              We use administrative, technical, and physical security measures to help protect your 
              personal information. While we have taken reasonable steps to secure the personal information 
              you provide to us, please be aware that despite our efforts, no security measures are 
              perfect or impenetrable, and no method of data transmission can be guaranteed against any 
              interception or other type of misuse.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Policy for Children</SectionTitle>
          <SectionContent>
            <p>
              We do not knowingly solicit information from or market to children under the age of 13. 
              If you become aware of any data we have collected from children under age 13, please 
              contact us using the contact information provided below.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Your Rights</SectionTitle>
          <SectionContent>
            <p>
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul>
              <li>The right to access – You have the right to request copies of your personal data</li>
              <li>The right to rectification – You have the right to request correction of inaccurate data</li>
              <li>The right to erasure – You have the right to request deletion of your personal data</li>
              <li>The right to restrict processing – You have the right to request restriction of processing</li>
              <li>The right to object to processing – You have the right to object to our processing</li>
              <li>The right to data portability – You have the right to request transfer of your data</li>
            </ul>
            <p>
              If you make a request, we have one month to respond to you. If you would like to exercise 
              any of these rights, please contact us at the information provided below.
            </p>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>California Privacy Rights</SectionTitle>
          <SectionContent>
            <p>
              California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits 
              our users who are California residents to request and obtain from us, once a year and free 
              of charge, information about categories of personal information (if any) we disclosed to 
              third parties for direct marketing purposes and the names and addresses of all third parties 
              with which we shared personal information in the immediately preceding calendar year.
            </p>
          </SectionContent>
        </Section>

        <ContactBox>
          <ContactTitle>Questions About This Policy?</ContactTitle>
          <ContactText>
            If you have questions or comments about this Privacy Policy, please contact us at:
          </ContactText>
          <ContactEmail href="mailto:privacy@carbuzz.fit">
            privacy@carbuzz.fit
          </ContactEmail>
        </ContactBox>
      </ContentContainer>
    </PageContainer>
  );
};

export default PrivacyPage;
